import { useState, useEffect, useRef } from 'react';
import { buildDefaultToolConfig } from '../hooks/useSessions';
import ToolConfigTable from './ToolConfigTable';
import { useModalDrag } from '../hooks/useModalDrag';

const TABS = ['General', 'Tools'];

export default function TopicSettingsModal({ session, tools = [], defaultSystemPrompt, onSave, onClose }) {
  const { boxRef, onDragStart }         = useModalDrag();
  const [activeTab, setActiveTab]       = useState('General');
  const [name, setName]                 = useState(session.name || session.id);
  const [saveToKb, setSaveToKb]         = useState(session.saveToKbByDefault ?? false);
  const [systemPrompt, setSystemPrompt] = useState(session.system_prompt ?? null);
  const [toolConfig, setToolConfig]     = useState(
    session.toolConfig ?? buildDefaultToolConfig(tools)
  );
  const isCustom     = systemPrompt !== null;
  const nameInputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave({
      name: trimmed,
      saveToKbByDefault: saveToKb,
      systemPrompt: isCustom ? (systemPrompt.trim() || null) : null,
      toolConfig,
    });
  };

  const handleToggleEnabled = (toolName) => {
    const current = toolConfig[toolName] ?? { enabled: true, forced: false };
    const nextEnabled = !current.enabled;
    setToolConfig(prev => ({
      ...prev,
      [toolName]: { enabled: nextEnabled, forced: nextEnabled ? current.forced : false },
    }));
  };

  const handleToggleForced = (toolName) => {
    const current = toolConfig[toolName] ?? { enabled: true, forced: false };
    const nextForced = !current.forced;
    setToolConfig(prev => ({
      ...prev,
      [toolName]: { enabled: nextForced ? true : current.enabled, forced: nextForced },
    }));
  };

  // Count disabled tools for the tab badge
  const disabledCount = tools.filter(t => {
    const cfg = toolConfig[t.name] ?? { enabled: true };
    return !cfg.enabled;
  }).length;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div ref={boxRef} className="modal-box modal-box--wide" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-header" onMouseDown={onDragStart}>
          <h2 className="modal-title" id="modal-title">Topic Settings</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>close</span>
          </button>
        </div>

        {/* Tab strip */}
        <div className="modal-tabs">
          {TABS.map(tab => (
            <button
              key={tab}
              type="button"
              className={`modal-tab ${activeTab === tab ? 'modal-tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
              {tab === 'Tools' && disabledCount > 0 && (
                <span className="modal-tab-badge">{disabledCount} off</span>
              )}
            </button>
          ))}
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>

          {/* ── General tab ─────────────────────────────────────────────── */}
          {activeTab === 'General' && (
            <>
              {/* Topic name */}
              <div className="modal-field">
                <label className="modal-label" htmlFor="topic-name">Topic name</label>
                <input
                  ref={nameInputRef}
                  id="topic-name"
                  className="modal-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter topic name…"
                  onKeyDown={e => e.key === 'Escape' && onClose()}
                />
              </div>

              {/* System prompt */}
              <div className="modal-field">
                <div className="modal-system-prompt-header">
                  <div>
                    <span className="modal-label">System prompt</span>
                    {!isCustom
                      ? <span className="modal-badge modal-badge--default">Using default</span>
                      : <span className="modal-badge modal-badge--custom">Custom</span>
                    }
                  </div>
                  {!isCustom ? (
                    <button type="button" className="modal-link-btn" onClick={() => setSystemPrompt(defaultSystemPrompt || '')}>
                      Customize
                    </button>
                  ) : (
                    <button type="button" className="modal-link-btn modal-link-btn--danger" onClick={() => setSystemPrompt(null)}>
                      Reset to default
                    </button>
                  )}
                </div>
                {isCustom ? (
                  <textarea
                    className="modal-textarea"
                    value={systemPrompt}
                    onChange={e => setSystemPrompt(e.target.value)}
                    placeholder="Enter a custom system prompt for this topic…"
                    rows={8}
                    spellCheck={false}
                  />
                ) : (
                  <div className="modal-system-prompt-preview">
                    {defaultSystemPrompt || 'Loading…'}
                  </div>
                )}
              </div>

              {/* Save to KB toggle */}
              <div className="modal-field">
                <div className="modal-toggle-row">
                  <div className="modal-toggle-info">
                    <span className="modal-label">Save to KB by default</span>
                    <span className="modal-hint">
                      When enabled, all messages in this topic are automatically saved to long-term memory.
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={saveToKb}
                    className={`toggle-switch ${saveToKb ? 'toggle-on' : ''}`}
                    onClick={() => setSaveToKb(v => !v)}
                  >
                    <span className="toggle-thumb" />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Tools tab ───────────────────────────────────────────────── */}
          {activeTab === 'Tools' && (
            <div className="modal-field">
              <ToolConfigTable
                tools={tools}
                toolConfig={toolConfig}
                onToggleEnabled={handleToggleEnabled}
                onToggleForced={handleToggleForced}
              />
              <p className="modal-hint" style={{ marginTop: '6px' }}>
                <strong>Enabled</strong> — tool is available to the AI.&nbsp;
                <strong>Forced</strong> — AI must call this tool on the first round.
              </p>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="modal-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-btn-save">Save</button>
          </div>

        </form>
      </div>
    </div>
  );
}
